/**
 * Shared helpers for encoding / decoding chat media message bodies.
 * Used by both REST history and the live XMPP service.
 *
 * Ported from `src/chat/mediaUtils.js` (see chat_logic.md §7).
 */

export type ChatSubject = 'chat_text' | 'chat_image' | 'chat_audio' | 'chat_video';

export const KNOWN_SUBJECTS: readonly ChatSubject[] = [
  'chat_text',
  'chat_image',
  'chat_audio',
  'chat_video',
];

export interface DecodedMessageBody {
  text: string;
  type: ChatSubject | string;
  mediaUrl: string | null;
  thumbUrl: string | null;
  mediaWidth: number | null;
  mediaHeight: number | null;
  duration: number | null;
  mediaSizeInByte: number | null;
}

export interface MediaPayload {
  s3MediaUrl: string;
  s3MediaThumbnailUrl?: string | null;
  width?: number | null;
  height?: number | null;
  duration?: number | null;
  mediaSizeInByte?: number | null;
}

/**
 * Decodes a base-64 string encoded with `btoa(unescape(encodeURIComponent(str)))`
 * (the convention used by iOS / XMPP). Falls back to plain `atob`.
 */
function b64DecodeUtf8(b64: string): string {
  try {
    return decodeURIComponent(escape(atob(b64)));
  } catch {
    return atob(b64);
  }
}

const empty = (subject: string): DecodedMessageBody => ({
  text: '',
  type: subject,
  mediaUrl: null,
  thumbUrl: null,
  mediaWidth: null,
  mediaHeight: null,
  duration: null,
  mediaSizeInByte: null,
});

export function decodeMessageBody(rawBody: string, subject: string): DecodedMessageBody {
  if (!rawBody) return empty(subject);

  try {
    const decoded = b64DecodeUtf8(rawBody);

    if (
      subject === 'chat_image' ||
      subject === 'chat_audio' ||
      subject === 'chat_video' ||
      decoded.trimStart().startsWith('{')
    ) {
      const parsed = JSON.parse(decoded) as Record<string, unknown>;

      const pickStr = (...keys: string[]): string | null => {
        for (const k of keys) {
          const v = parsed[k];
          if (typeof v === 'string' && v.trim()) return v;
        }
        return null;
      };
      const pickNum = (...keys: string[]): number | null => {
        for (const k of keys) {
          const v = parsed[k];
          if (v != null && Number.isFinite(Number(v))) return Number(v);
        }
        return null;
      };

      let text = '';
      let mediaUrl: string | null = null;
      let thumbUrl: string | null = null;
      let mediaWidth: number | null = null;
      let mediaHeight: number | null = null;
      let duration: number | null = null;
      let mediaSizeInByte: number | null = null;

      if (subject === 'chat_image') {
        mediaUrl = pickStr('s3MediaUrl', 'url', 'file_url');
        thumbUrl = pickStr('s3MediaThumbnailUrl') ?? mediaUrl;
        mediaWidth = pickNum('width');
        mediaHeight = pickNum('height');
        text = '📷 Image';
      } else if (subject === 'chat_audio') {
        mediaUrl = pickStr('s3MediaUrl', 'url', 'file_url');
        duration = pickNum('duration');
        text = '🎵 Audio';
      } else if (subject === 'chat_video') {
        mediaUrl = pickStr('s3MediaUrl', 'url', 'file_url');
        thumbUrl = pickStr('s3MediaThumbnailUrl');
        mediaWidth = pickNum('width');
        mediaHeight = pickNum('height');
        duration = pickNum('duration');
        mediaSizeInByte = pickNum('mediaSizeInByte');
        text = '🎬 Video';
      } else {
        text = (typeof parsed.text === 'string' && parsed.text) || decoded;
      }

      return {
        text,
        type: subject,
        mediaUrl,
        thumbUrl,
        mediaWidth,
        mediaHeight,
        duration,
        mediaSizeInByte,
      };
    }

    return { ...empty(subject), text: decoded };
  } catch {
    return { ...empty(subject), text: rawBody };
  }
}

/**
 * Builds a base-64 encoded JSON payload for sending a media message body.
 */
export function encodeMediaBody(
  subject: 'chat_image' | 'chat_audio' | 'chat_video',
  mediaPayload: MediaPayload,
): string {
  const payload: Record<string, unknown> = { s3MediaUrl: mediaPayload.s3MediaUrl || '' };
  if (subject === 'chat_video') {
    // Match iOS sendChatVideoMessage exactly. Width/height are returned by
    // processing but are not part of the final chat_video XMPP body.
    payload.s3MediaThumbnailUrl = mediaPayload.s3MediaThumbnailUrl || '';
    payload.duration = mediaPayload.duration ?? 0;
    payload.mediaSizeInByte = mediaPayload.mediaSizeInByte ?? 0;
  } else if (subject === 'chat_audio') {
    payload.duration = mediaPayload.duration ?? 0;
  } else {
    payload.s3MediaThumbnailUrl = mediaPayload.s3MediaThumbnailUrl || null;
    payload.width = mediaPayload.width ?? null;
    payload.height = mediaPayload.height ?? null;
  }
  return btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
}
